import { useEffect, useRef, useState, type ReactNode } from "react";
import { KeyRound, LogOut } from "lucide-react";
import { NavLink, useLocation, useNavigationType } from "react-router-dom";

import { NavSidebar } from "./NavSidebar";
import { NotificationBell } from "./NotificationBell";
import { useAuth } from "../auth/AuthContext";
import { useProductBranding } from "../lib/branding";
import { formatRoles } from "../lib/roles";
import { readSidebarCollapsed, writeSidebarCollapsed } from "../nav/sidebarState";

/**
 * Положение прокрутки рабочей области по записям истории браузера (D-48, CR-5 пакет 2).
 *
 * ЗАЧЕМ. Прокруткой владеет рабочая область (пакет 1), а браузер восстанавливает положение
 * только у прокрутки самой страницы. Из-за этого переход «реестр → карточка → Назад» открывал
 * реестр сверху: на длинном списке место приходилось искать заново. Это ухудшение внесено
 * решением самого CR-5, поэтому и чинится внутри него, а не откладывается.
 *
 * КЛЮЧ — `location.key`, А НЕ АДРЕС: в один и тот же реестр заходят много раз, и это разные
 * записи истории со своим положением у каждой. Живёт в памяти вкладки: закрыли вкладку —
 * забыли, на сервер ничего не уходит (как и свёрнутость панели).
 */
const scrollByHistoryKey = new Map<string, number>();

/** Сколько записей истории помним. Глубже пользователь кнопкой «Назад» практически не ходит. */
const SCROLL_MEMORY_LIMIT = 50;

/**
 * Каркас интерфейса после входа (SK-5, переработан CR-5 — решение владельца 07.09.2026):
 * графитовая шапка во всю ширину окна, слева — боковая панель разделов по ролям (Р-13),
 * справа — рабочая область с потолком ширины `--w-work`.
 * Оформление — строго по токенам docs/07 (без «сырых» hex и «сырых» чисел ширины), UI на русском.
 *
 * ПРОКРУТКОЙ ВЛАДЕЕТ ОБОЛОЧКА, А НЕ СТРАНИЦА, и это осознанная плата за закреплённые шапки
 * таблиц (пакет 2 CR-5): пока прокручивался документ, каждая таблица сидела в своей обёртке с
 * `overflow-x-auto`, а такая обёртка перехватывает прокрутку — заголовок «прилипал» бы к ней и
 * уезжал вместе со страницей. Здесь высота ограничена окном (`.app-shell`), прокручивается
 * `main` (`.app-main`), и `position: sticky` у строки заголовков наконец означает то, что
 * написано. НА ПЕЧАТИ обе меры снимаются правилом в `styles/index.css` — иначе на лист уходил бы
 * только первый экран реестра (docs/07 §5 обещает печать реестров целиком, стережёт tokens.test).
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const branding = useProductBranding();
  const location = useLocation();
  const roles = user?.roles ?? [];
  const mainRef = useRef<HTMLElement>(null);
  const lastPathRef = useRef(location.pathname);
  const navigationType = useNavigationType();
  // Состояние панели читается ОДИН раз при создании: обращение к хранилищу в теле рендера
  // повторялось бы на каждую перерисовку, а меняется оно только по нажатию кнопки.
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);

  const toggleSidebar = () => {
    setCollapsed((previous) => {
      const next = !previous;
      writeSidebarCollapsed(next);
      return next;
    });
  };

  // Положение рабочей области: назад — вернуть, в новый раздел — в начало, отбор в том же
  // разделе — не трогать.
  //
  // ПОЧЕМУ ТРИ СЛУЧАЯ, А НЕ ОДИН. Со своим контейнером прокрутки браузер не делает ничего сам:
  // новый экран открывался бы прокрученным до середины предыдущего (отсюда сброс), а «Назад»
  // приводил бы в начало списка вместо той строки, с которой ушли (отсюда возврат, D-48).
  // Отборы реестра и подсветка строки (`?obl=…` из сводки и дашборда) меняют только параметры
  // запроса — там позицию сбрасывать нельзя, именно туда пользователя и привели.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const saved = navigationType === "POP" ? scrollByHistoryKey.get(location.key) : undefined;

    if (saved === undefined) {
      // Записи нет (в тот раз экран не листали) — ведём себя как обычный переход. Ранний выход
      // здесь был бы ХУЖЕ, чем до пакета 2: новый экран открылся бы на смещении предыдущего.
      if (location.pathname !== lastPathRef.current) main.scrollTop = 0;
      return;
    }

    // Список ещё грузится, высоты не хватает — браузер зажмёт значение в ноль. Повторяем
    // попытку, пока содержимое растёт, но не дольше секунды: дальше это уже не возврат к
    // прежнему месту, а прыжок под руками у читающего.
    const deadline = Date.now() + 1000;
    let frame = 0;
    const restore = () => {
      const el = mainRef.current;
      if (!el) return;
      el.scrollTop = saved;
      if (Math.abs(el.scrollTop - saved) > 1 && Date.now() < deadline) {
        frame = requestAnimationFrame(restore);
      }
    };
    restore();
    // Отмена обязательна: без неё доживший цикл спорит и со следующим переходом (тот ставит
    // ноль, цикл возвращает прежнее смещение и записывает его под ЧУЖОЙ ключ истории), и с
    // самим пользователем, если он начал листать раньше, чем список дорос.
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [location.key, location.pathname, navigationType]);

  // Текущий раздел помним отдельно: сравнение «сменился ли раздел» должно переживать смену
  // только параметров запроса.
  useEffect(() => {
    lastPathRef.current = location.pathname;
  }, [location.pathname]);

  // Положение пишем на самой прокрутке, а не при уходе с экрана: к моменту размонтирования
  // содержимое уже меняется, и `scrollTop` успевает обнулиться самим браузером.
  const rememberScroll = () => {
    if (!mainRef.current) return;
    scrollByHistoryKey.set(location.key, mainRef.current.scrollTop);
    // Map хранит порядок вставки: самая старая запись уходит первой.
    if (scrollByHistoryKey.size > SCROLL_MEMORY_LIMIT) {
      const oldest = scrollByHistoryKey.keys().next().value;
      if (oldest !== undefined) scrollByHistoryKey.delete(oldest);
    }
  };

  return (
    <div className="app-shell flex h-screen flex-col bg-bg text-ink">
      {/* Шапка и навигация на печать не идут (E9a): печатается содержимое экрана, а не интерфейс. */}
      <header className="no-print shrink-0 border-b-[3px] border-accent bg-ink text-top-fg">
        {/* Во всю ширину окна (решение владельца 03.09.2026, №2): кнопки и колокольчик встают у
            правого края экрана, где их ищут, и панель уведомлений раскрывается от края, а не из
            середины широкого монитора. */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
          {/* Брендинг инстанса (D-10): крупно — имя продукта («Облигат» у нас, «АС «КИД»» у
              заказчика), мелко — постоянная расшифровка. Пока ответ сервера не пришёл, место
              пустует: подставлять своё имя нельзя — на инстансе заказчика это чужой бренд. */}
          <div>
            {/* Капс — оформление (text-transform), не данные: в конфигурации и в ответе api
                имя лежит как написано, чтобы закладки и колонтитулы сохраняли свой регистр. */}
            <div className="doc-title text-[20px] font-bold uppercase tracking-[0.06em]">
              {branding?.name ?? ""}
            </div>
            {/* Расшифровка имени продукта — ПРОПИСНЫМИ (решение владельца 10.09.2026).
                Капс делает оформление, а не данные: в конфигурации и в ответе сервера строка
                лежит как написана, и в заголовок вкладки, закладки и колонтитулы уходит своим
                регистром. Разрядка обязательна — прописные без неё читаются сплошной полосой;
                та же пара классов, что у заголовков групп боковой панели. */}
            <div className="text-[12px] uppercase tracking-wide text-top-muted">
              {branding?.descriptor ?? ""}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <div className="text-right">
                <div className="text-[13px] font-bold">{user.fullName}</div>
                <div className="text-[12px] text-top-muted">{formatRoles(roles)}</div>
              </div>
            )}
            {/* Центр уведомлений (E10, ТЗ 4.1.4): единое место для напоминаний о сроках,
                просрочек и правовых сигналов. Что показать, решает сервер — по ролям (Прил. Г). */}
            {user && <NotificationBell />}
            {/* Смена своего пароля (D-15). Не раздел навигации: действие над своей учётной
                записью, а не рабочий экран, — поэтому стоит рядом с «Выйти», а не в разделах. */}
            {user && (
              <NavLink
                to="/change-password"
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-m)] border border-top-field-border bg-top-field px-3 py-1.5 text-[13px] text-top-fg hover:text-surface"
              >
                <KeyRound size={16} aria-hidden="true" />
                Сменить пароль
              </NavLink>
            )}
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-m)] border border-top-field-border bg-top-field px-3 py-1.5 text-[13px] text-top-fg hover:text-surface"
            >
              <LogOut size={16} aria-hidden="true" />
              Выйти
            </button>
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <NavSidebar roles={roles} collapsed={collapsed} onToggle={toggleSidebar} />

        {/* Рабочая область: прокручивается сама (см. пояснение выше) и держит потолок ширины
            `--w-work` = 1600 px (решение владельца 07.09.2026, docs/29 §2.1). Поля по краям
            растут вместе с монитором — вид приложения на 1920 и на 2560 одинаковый. */}
        <main
          ref={mainRef}
          onScroll={rememberScroll}
          className="app-main min-w-0 flex-1 overflow-auto"
        >
          <div className="mx-auto max-w-[var(--w-work)] px-6 py-5">{children}</div>
        </main>
      </div>
    </div>
  );
}
